import { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router';
import { useAuth } from '@/utils/Auth';
import { Button } from '@/components/ui/button';
import { Menu, Info } from 'lucide-react';
import { useFirebase } from '../../../context/FirebaseContext';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from 'react-i18next';
import defaultLogo from '/favicon.png';
import ApprovalsTour from './ApprovalsTour';

const getSubdomainFromUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost') return 'APP';
  const parts = hostname.split('.');
  return parts.length > 0 ? parts[0].toUpperCase() : 'APP';
};

export default function Component() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { signOut } = useAuth();
  const {
    role,
    first,
    clearOnBehalfMode,
    isOnBehalfMode,
    originalCompanyID,
    originalCompanyName,
    setCompanyID,
    setCompanyName,
    name,
    handleFirstLogin,
  } = useFirebase(); // State management
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [currentLogoSrc, setCurrentLogoSrc] = useState<string>('');
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [lastPath, setLastPath] = useState(location.pathname);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [tourShown, setTourShown] = useState(false); // Local state to track if tour has been shown

  console.log('first4', first);

  useEffect(() => {
    const currentPath = location.pathname.endsWith('/')
      ? location.pathname.slice(0, -1)
      : location.pathname;

    // Only handle local state cleanup
    if (currentPath !== '/company-management') {
      setCompanyID(originalCompanyID), setCompanyName(originalCompanyName);
    }
  }, [
    location.pathname,
    originalCompanyID,
    originalCompanyName,
    setCompanyID,
    setCompanyName,
  ]);

  // Clear on-behalf mode when route changes
  useEffect(() => {
    const currentPath = location.pathname.endsWith('/')
      ? location.pathname.slice(0, -1)
      : location.pathname;

    const previousPath = lastPath.endsWith('/')
      ? lastPath.slice(0, -1)
      : lastPath;

    // Only clear if we're navigating away from /myday
    if (previousPath === '/myday' && currentPath !== '/myday') {
      const onBehalfData = sessionStorage.getItem('authContextState_onBehalf');
      if (onBehalfData && clearOnBehalfMode) {
        clearOnBehalfMode();
      }
    }

    setLastPath(location.pathname);
  }, [location.pathname, clearOnBehalfMode]);

  // Get current path without trailing slash
  const currentPath = location.pathname.endsWith('/')
    ? location.pathname.slice(0, -1)
    : location.pathname;

  // const isResetPasswordPage = currentPath.startsWith('/reset-password');

  // Logo handling
  const subdomain = getSubdomainFromUrl();
  const envKey = `VITE_LOGO_URL_${subdomain.replace('-', '_')}`;
  const logoUrl = (import.meta.env[envKey] as string) || '/favicon.png';

  // Check if acting on behalf of another user
  const onBehalfStorage = sessionStorage.getItem('authContextState_onBehalf');
  const isActingOnBehalfOf = !!onBehalfStorage || !!isOnBehalfMode;
  console.log('Is acting on behalf of another user:', isOnBehalfMode);

  const handleImageError = () => {
    setLogoError(true);
    setCurrentLogoSrc(defaultLogo);
  };

  const handleImageLoad = () => {
    setCurrentLogoSrc(logoUrl);
  };

  const getLogoSrc = () => {
    return !logoUrl || logoError ? defaultLogo : logoUrl;
  };

  // Effect to close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const profileButton = document.getElementById('profile-button');
      const profileMenu = document.getElementById('profile-menu');

      if (
        isProfileOpen &&
        profileButton &&
        profileMenu &&
        !profileButton.contains(event.target as Node) &&
        !profileMenu.contains(event.target as Node)
      ) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileOpen]);

  // Tour handling - Show tour for first-time users when they reach /myday
  useEffect(() => {
    if (first === true && !tourShown && currentPath === '/myday') {
      // Add a small delay to ensure the page is fully loaded
      const tourTimer = setTimeout(() => {
        setIsTourOpen(true);
        setIsMenuOpen(false);
        setTourShown(true); // Mark that tour has been shown
      }, 100);

      return () => clearTimeout(tourTimer);
    }
  }, [first, tourShown, currentPath]);

  // Close menu on user state change or when user logs in
  useEffect(() => {
    // Always ensure menu is closed when user state changes (login/logout)
    setIsMenuOpen(false);
  }, [user]);

  // Reset UI state when the component mounts or when user logs in
  useEffect(() => {
    if (user) {
      // Ensure menu is closed on login
      setIsMenuOpen(false);
    }
  }, []);

  const handleTourClose = async () => {
    setIsTourOpen(false);

    // If this is a first-time user completing the tour
    if (first && handleFirstLogin) {
      try {
        // Update the database to mark first login as complete
        await handleFirstLogin();
        console.log('First login completed and database updated');
      } catch (error) {
        console.error('Error updating first login status:', error);
      }
    }
  };
  const handleLogout = async () => {
    // Close the mobile menu
    setIsMenuOpen(false);
    // Close profile dropdown if it's open
    setIsProfileOpen(false);
    // Sign out the user
    await signOut();
  };
  const handleOpenNoticeSimple = () => {
    navigate('/mydocs');
    setIsProfileOpen(false);
  };

  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  const isActivePath = (path: string) => {
    return (
      currentPath === path ||
      (path === '/myday' && currentPath === '/myday') ||
      (path === '/' && currentPath === '')
    );
  };

  // Function to get user initials
  const getUserInitials = () => {
    if (!name) return 'U';
    const nameParts = name
      .trim()
      .split(' ')
      .filter((part: string) => part.length > 0);
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${
        nameParts[nameParts.length - 1][0]
      }`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  return (
    <>
      {currentPath === '/myday' && (
        <ApprovalsTour isOpen={isTourOpen} onClose={handleTourClose} />
      )}
      <nav className="fixed top-0 left-0 right-0 bg-gray-900 text-white shadow-md z-50">
        <div className="mx-auto px-4 sm:px-6">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Link to="/" className="flex items-center h-16">
                <div className="h-full flex items-center">
                  <img
                    alt="Company Logo"
                    style={{ maxHeight: '52%', width: 'auto' }}
                    src={getLogoSrc()}
                    onError={handleImageError}
                    onLoad={handleImageLoad}
                    className="object-contain"
                  />
                </div>
                {currentLogoSrc === defaultLogo && (
                  <span className="ml-2 text-lg font-medium">VionHR</span>
                )}
              </Link>
            </div>

            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-4">
                {user && (
                  <>
                    <Link
                      to="/"
                      className={`px-3 py-2 text-base font-medium ${
                        isActivePath('/')
                          ? 'border-b-2 border-red-500'
                          : 'hover:text-gray-500'
                      }`}
                      onClick={handleLinkClick}
                    >
                      {t('NavBar.home')}
                    </Link>
                    <Link
                      to="/myday"
                      className={`px-3 py-2 text-base font-medium ${
                        isActivePath('/myday')
                          ? 'border-b-2 border-red-500'
                          : 'hover:text-gray-500'
                      }`}
                      onClick={handleLinkClick}
                    >
                      {t('NavBar.mydays')}
                    </Link>
                    <Link
                      to="/mydepartment"
                      className={`px-3 py-2 text-base font-medium ${
                        isActivePath('/mydepartment')
                          ? 'border-b-2 border-red-500'
                          : 'hover:text-gray-500'
                      }`}
                      onClick={handleLinkClick}
                    >
                      {t('NavBar.mydeparmant')}
                    </Link>
                    {(role === 'Admin' ||
                      role === 'SuperAdmin' ||
                      role === 'Approver' ||
                      isActingOnBehalfOf) && (
                      <Link
                        to="/approvals"
                        className={`px-3 py-2 text-base font-medium ${
                          isActivePath('/approvals')
                            ? 'border-b-2 border-red-500'
                            : 'hover:text-gray-500'
                        }`}
                        onClick={handleLinkClick}
                      >
                        {t('NavBar.approvals')}
                      </Link>
                    )}
                    {(role === 'Admin' ||
                      role === 'SuperAdmin' ||
                      isActingOnBehalfOf) && (
                      <Link
                        to="/company-management"
                        className={`px-3 py-2 text-base font-medium ${
                          isActivePath('/company-management')
                            ? 'border-b-2 border-red-500'
                            : 'hover:text-gray-500'
                        }`}
                        onClick={handleLinkClick}
                      >
                        {t('NavBar.companymanagement')}
                      </Link>
                    )}
                    {role === 'SuperAdmin' && (
                      <Link
                        to="/company-registration"
                        className={`px-3 py-2 text-base font-medium ${
                          isActivePath('/company-registration')
                            ? 'border-b-2 border-red-500'
                            : 'hover:text-gray-500'
                        }`}
                        onClick={handleLinkClick}
                      >
                        {t('NavBar.companyregistration')}
                      </Link>
                    )}
                  </>
                )}
              </div>

              <div className="flex items-center space-x-4">
                {user ? (
                  <>
                    {isActivePath('/myday') ? (
                      <Button
                        id="info-button"
                        className="hover:text-gray-500 w-14 h-10"
                        onClick={() => {
                          setIsTourOpen(true);
                          setIsMenuOpen(false);
                        }}
                        title="Show tour"
                      >
                        <Info />
                      </Button>
                    ) : (
                      <div className="w-14 h-10" />
                    )}{' '}
                    <LanguageSelector />
                    <div className="relative">
                      <button
                        id="profile-button"
                        className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white font-medium"
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                      >
                        {getUserInitials()}
                      </button>
                      {isProfileOpen && (
                        <div
                          id="profile-menu"
                          className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50"
                        >
                          <div className="px-4 py-2 text-sm text-gray-700 border-b">
                            {name || user?.email || 'User'}
                            <div className="text-xs text-gray-500">
                              {user?.email}
                            </div>
                          </div>
                          <div className="py-1">
                            <button
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={handleOpenNoticeSimple}
                            >
                              {t('NavBar.myDocs')}
                            </button>
                            <button
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={handleLogout}
                            >
                              {t('NavBar.logout')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <LanguageSelector />
                )}
              </div>
              <div className="md:hidden">
                <button
                  type="button"
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                  <span className="sr-only">Open main menu</span>
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {user && (
                <>
                  <Link
                    to="/"
                    className={`block px-3 py-2 rounded-md text-base font-medium ${
                      isActivePath('/')
                        ? 'bg-gray-700 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                    onClick={handleLinkClick}
                  >
                    {t('NavBar.home')}
                  </Link>
                  <Link
                    to="/myday"
                    className={`block px-3 py-2 rounded-md text-base font-medium ${
                      isActivePath('/myday')
                        ? 'bg-gray-700 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                    onClick={handleLinkClick}
                  >
                    {t('NavBar.mydays')}
                  </Link>
                  <Link
                    to="/mydepartment"
                    className={`block px-3 py-2 rounded-md textBase font-medium ${
                      isActivePath('/mydepartment')
                        ? 'bg-gray-700 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                    onClick={handleLinkClick}
                  >
                    {t('NavBar.mydeparmant')}
                  </Link>
                  {(role === 'Admin' ||
                    role === 'SuperAdmin' ||
                    role === 'Approver' ||
                    isActingOnBehalfOf) && (
                    <Link
                      to="/approvals"
                      className={`block px-3 py-2 rounded-md textBase font-medium ${
                        isActivePath('/approvals')
                          ? 'bg-gray-700 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                      onClick={handleLinkClick}
                    >
                      {t('NavBar.approvals')}
                    </Link>
                  )}
                  {(role === 'Admin' ||
                    role === 'SuperAdmin' ||
                    isActingOnBehalfOf) && (
                    <Link
                      to="/company-management"
                      className={`block px-3 py-2 rounded-md textBase font-medium ${
                        isActivePath('/company-management')
                          ? 'bg-gray-700 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                      onClick={handleLinkClick}
                    >
                      {t('NavBar.companymanagement')}
                    </Link>
                  )}
                  {role === 'SuperAdmin' && (
                    <Link
                      to="/company-registration"
                      className={`block px-3 py-2 rounded-md textBase font-medium ${
                        isActivePath('/company-registration')
                          ? 'bg-gray-700 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                      onClick={handleLinkClick}
                    >
                      {t('NavBar.companyregistration')}
                    </Link>
                  )}
                  {/* <Link
                    to="/company-registration"
                    className={`block px-3 py-2 rounded-md textBase font-medium ${
                      activeLink === '/company-registration'
                        ? 'bg-gray-700 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                    onClick={() => handleLinkClick('/company-registration')}
                  >
                    {t('NavBar.companyregistration')}
                  </Link> */}{' '}
                  <div className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white font-medium mr-3">
                        {getUserInitials()}
                      </div>
                      <div>
                        <div className="text-sm text-white">
                          {name || user?.email || 'User'}
                        </div>
                        <div className="text-xs text-gray-300">
                          {user?.email}
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button
                    className="bg-red-500 hover:bg-red-600 font-medium text-white w-full mt-2"
                    onClick={handleLogout}
                  >
                    {t('NavBar.logout')}
                  </Button>
                </>
              )}
            </div>{' '}
          </div>
        )}{' '}
      </nav>
    </>
  );
}
